<!--
 * @Author: Christion.Young
 * @Date: 2025-09-18 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-09-18 10:00:00
 * @Description: 聊天记录页面 - 简化版聊天历史记录列表
 * @Features:
 *   - 基于 messageChatList() 接口的聊天列表展示
 *   - 只显示用户名和未读消息数
 *   - 点击聊天项打开全局聊天组件
 *   - 红色主题设计，与全局样式一致
 * @DataStructure:
 *   - userId: "1937750359313367041" (聊天对象用户ID)
 *   - username: "马斯克" (聊天对象用户名)
 *   - unreadCount: "2" (未读消息数，字符串格式)
-->
<template>
  <div class="chat-history-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">聊天记录</h1>
      </div>
    </div>

    <!-- 聊天列表内容 -->
    <div class="chat-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-section">
        <div class="loading-spinner">
          <i class="el-icon-loading"></i>
          <span>正在加载聊天记录...</span>
        </div>
      </div>

      <!-- 聊天列表 -->
      <div v-else-if="chatList.length > 0" class="chat-list">
        <div
          v-for="chat in chatList"
          :key="chat.userId"
          class="chat-item"
          @click="openChat(chat)"
        >
          <!-- 用户头像 -->
          <div class="chat-avatar">
            <span class="avatar-text">{{ getAvatarText(chat.nickname) }}</span>
          </div>

          <!-- 聊天信息 -->
          <div class="chat-info">
            <div class="chat-header">
              <h3 class="user-name">{{ chat.nickname }}</h3>
              <div class="message-icon">
                <i class="el-icon-chat-dot-round"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <i class="el-icon-chat-dot-round"></i>
        </div>
        <h3>暂无聊天记录</h3>
        <p>您还没有与任何客服进行过对话</p>
        <button class="go-services-btn" @click="goToServices">
          <i class="el-icon-s-order"></i>
          浏览服务
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/api";

export default {
  name: "ChatHistory",
  data() {
    return {
      // 加载状态
      loading: false,
      // 聊天列表
      chatList: [],
    };
  },
  computed: {
    /**
     * 用户信息
     */
    userInfo() {
      return this.$store.getters["user/userInfo"] || {};
    },

    /**
     * 是否已登录
     */
    isLoggedIn() {
      return this.$store.getters["user/isLoggedIn"];
    },
  },
  mounted() {
    this.checkLoginAndLoad();
  },
  methods: {
    /**
     * 检查登录状态并加载数据
     */
    checkLoginAndLoad() {
      if (!this.isLoggedIn) {
        this.$msg.warning("请先登录后查看聊天记录");
        this.$router.push("/");
        return;
      }

      this.loadChatList();
    },

    /**
     * 加载聊天列表 - 调用 messageChatList() 接口
     */
    async loadChatList() {
      this.loading = true;
      try {
        const response = await API.message.messageChatList();

        if (response && response.code === 0 && response.data) {
          this.chatList = this.formatChatListFromAPI(response.data);
          console.log("聊天列表加载成功:", this.chatList);
        } else {
          this.chatList = [];
          const errorMsg = response?.msg || "未知错误";
          console.warn("获取聊天列表失败:", errorMsg);
          if (response?.msg) {
            this.$msg.warning(errorMsg);
          }
        }
      } catch (error) {
        console.error("加载聊天列表失败:", error);
        this.$msg.error("加载聊天记录失败，请稍后重试");
        this.chatList = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * 格式化聊天列表数据
     * @param {Array} rawList - 原始API返回数据
     */
    formatChatListFromAPI(rawList) {
      if (!Array.isArray(rawList)) {
        return [];
      }

      return rawList.map((item) => ({
        userId: item.userId,
        nickname: item.username || "客服",
      }));
    },

    /**
     * 获取聊天历史记录
     * @param {string} toUserId - 聊天对象用户ID
     * @returns {Promise<Array>} 聊天历史记录数组
     */
    async loadChatHistory(toUserId) {
      try {
        // 获取当前用户ID - 优先从store中获取
        const currentUserId =
          this.userInfo?.id ||
          this.userInfo?.userId ||
          this.$store.state.user?.userInfo?.id ||
          this.$store.state.user?.userInfo?.userId;

        if (!currentUserId) {
          console.warn("无法获取当前用户ID，跳过获取聊天历史");
          return [];
        }

        console.log("正在获取聊天历史记录:", {
          fromUserId: currentUserId,
          toUserId,
        });

        // 调用聊天历史记录接口
        const response = await API.message.messageChatHistory(
          currentUserId,
          toUserId
        );

        if (response && response.code === 0 && response.data) {
          console.log(
            "聊天历史记录获取成功，共",
            response.data.length,
            "条记录"
          );
          return this.formatChatHistory(response.data);
        } else {
          console.warn("获取聊天历史失败:", response?.msg || "未知错误");
          return [];
        }
      } catch (error) {
        console.error("获取聊天历史记录失败:", error);
        return [];
      }
    },

    /**
     * 格式化聊天历史记录数据
     * @param {Array} historyList - 原始历史记录数据
     * @returns {Array} 格式化后的聊天记录
     */
    formatChatHistory(historyList) {
      if (!Array.isArray(historyList)) {
        return [];
      }

      return historyList.map((message) => ({
        id: message.id,
        fromUserId: message.fromUserId,
        fromUserName: message.fromUserName,
        toUserId: message.toUserId,
        toUserName: message.toUserName,
        content: message.content,
        msgType: message.msgType || "3", // 保持原始msgType，让聊天组件处理转换
        orderId: message.orderId,
        page: message.page,
        readStatus: message.readStatus,
        createTime: message.createTime,
        updateTime: message.updateTime,
        isRevoked: message.isRevoked || false,
        extraData: message.extraData || {},
        // 添加实际API返回的字段
        createId: message.createId,
        updateId: message.updateId,
        delFlag: message.delFlag,
        tenantId: message.tenantId,
        timeSort: message.timeSort,
      }));
    },

    /**
     * 打开聊天对话框
     * @param {Object} chat - 聊天项
     */
    async openChat(chat) {
      // 显示加载提示
      const closeLoading = this.$msg.loading("正在加载聊天记录...");

      try {
        // 先获取历史聊天记录
        const historyResponse = await this.loadChatHistory(chat.userId);

        // 关闭加载提示
        closeLoading();

        // 触发全局聊天组件显示，并传入历史记录
        this.$bus.emit("show-chat-dialog", {
          employeeId: chat.userId,
          employeeName: chat.nickname,
          chatType: "service",
          chatHistory: historyResponse || [], // 传入聊天历史记录
        });
      } catch (error) {
        // 关闭加载提示
        closeLoading();

        console.error("打开聊天失败:", error);

        // 即使获取历史记录失败，也要打开聊天框
        this.$bus.emit("show-chat-dialog", {
          employeeId: chat.userId,
          employeeName: chat.nickname,
          chatType: "service",
          chatHistory: [],
        });

        // 提示用户
        this.$msg.warning("获取聊天历史记录失败，但聊天功能正常");
      }
    },

    /**
     * 跳转到服务页面
     */
    goToServices() {
      this.$router.push("/services");
    },

    /**
     * 获取头像文字
     */
    getAvatarText(name) {
      return name ? name.charAt(0).toUpperCase() : "客";
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义 - 与Header.vue保持一致
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;

// 中性色彩
$text-primary: #2c3e50;
$text-secondary: #7f8c8d;
$border-color: #e1e8ed;

.chat-history-page {
  min-height: calc(100vh - 145px); // 减去顶部导航栏高度
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

/* 页面头部 */
.page-header {
  background: white;
  border-bottom: 1px solid $border-color;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .header-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 30px 20px;
  }

  .page-title {
    font-size: 28px;
    font-weight: 700;
    color: $text-primary;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
      content: "";
      width: 4px;
      height: 28px;
      background: linear-gradient(
        135deg,
        $primary-red 0%,
        $primary-red-dark 100%
      );
      border-radius: 2px;
    }
  }
}

/* 聊天内容区域 */
.chat-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
}

/* 加载状态 */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;

  .loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    color: $text-secondary;

    i {
      font-size: 32px;
      animation: rotate 1s linear infinite;
    }

    span {
      font-size: 14px;
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 聊天列表 */
.chat-list {
  display: grid;
  gap: 16px;
}

.chat-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid $border-color;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;

  &:hover {
    box-shadow: 0 4px 20px rgba(231, 76, 60, 0.15);
    border-color: $primary-red;
    transform: translateY(-2px);
  }

  .chat-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      $primary-red 0%,
      $primary-red-dark 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .avatar-text {
      color: white;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .chat-info {
    flex: 1;
    min-width: 0;

    .chat-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .user-name {
        font-size: 16px;
        font-weight: 600;
        color: $text-primary;
        margin: 0;
      }

      .message-icon {
        color: $primary-red;
        font-size: 18px;
        flex-shrink: 0;
        transition: all 0.2s ease;

        &:hover {
          color: $primary-red-dark;
          transform: scale(1.1);
        }

        i {
          font-size: 18px;
        }
      }
    }
  }
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;

  .empty-icon {
    margin-bottom: 20px;

    i {
      font-size: 64px;
      color: #ddd;
    }
  }

  h3 {
    font-size: 20px;
    color: $text-primary;
    margin: 0 0 10px 0;
  }

  p {
    color: $text-secondary;
    font-size: 14px;
    margin: 0 0 20px 0;
  }

  .go-services-btn {
    padding: 12px 24px;
    background: linear-gradient(
      135deg,
      $primary-red 0%,
      $primary-red-dark 100%
    );
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
      background: linear-gradient(
        135deg,
        $primary-red-dark 0%,
        $primary-red 100%
      );
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
    }

    i {
      font-size: 16px;
    }
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    .header-content {
      padding: 20px;
    }

    .page-title {
      font-size: 24px;

      &::before {
        height: 24px;
      }
    }
  }

  .chat-content {
    padding: 20px;
  }

  .chat-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    .chat-info .chat-header {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }
}

@media (max-width: 480px) {
  .chat-item {
    padding: 16px;

    .chat-avatar {
      width: 40px;
      height: 40px;

      .avatar-text {
        font-size: 16px;
      }
    }
  }
}
</style>
